import React, { useState, useEffect } from 'react';

const AlgorithmValidation = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    fetch('/algorithm_100_results.json')
      .then(res => res.json())
      .then(data => {
        setResults(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-purple-500 border-t-transparent mx-auto mb-6"></div>
          <p className="text-white text-xl">Loading Algorithm Results...</p>
        </div>
      </div>
    );
  }

  // Calculate accuracy stats
  const validResults = results.filter(r => r?.actual_data?.first_marriage_age && r?.predicted_marriage_age);
  const within2 = validResults.filter(r => Math.abs(r.predicted_marriage_age - r.actual_data.first_marriage_age) <= 2).length;
  const within5 = validResults.filter(r => Math.abs(r.predicted_marriage_age - r.actual_data.first_marriage_age) <= 5).length;
  const within10 = validResults.filter(r => Math.abs(r.predicted_marriage_age - r.actual_data.first_marriage_age) <= 10).length;

  const filteredResults = results.filter(person => 
    person?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort results
  const sortedResults = [...filteredResults].sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'accuracy') {
      const diffA = Math.abs((a.predicted_marriage_age || 0) - (a.actual_data?.first_marriage_age || 0));
      const diffB = Math.abs((b.predicted_marriage_age || 0) - (b.actual_data?.first_marriage_age || 0));
      return diffA - diffB;
    }
    if (sortBy === 'marriages') return (b.actual_data?.total_marriages || 0) - (a.actual_data?.total_marriages || 0);
    return 0;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Algorithm Validation
              </h1>
              <p className="text-gray-400 text-sm">Real Algorithm vs Historical Data - NO HARDCODING</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              >
                <option value="name">Sort by Name</option>
                <option value="accuracy">Sort by Accuracy</option>
                <option value="marriages">Sort by Marriages</option>
              </select>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Accuracy Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">📊 Algorithm Accuracy Report</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <p className="text-gray-400 text-sm">Total Analyzed</p>
              <p className="text-white text-3xl font-bold">{validResults.length}</p>
            </div>
            <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
              <p className="text-gray-400 text-sm">Within ±2 Years</p>
              <p className="text-green-400 text-3xl font-bold">{within2}</p>
              <p className="text-green-400 text-sm">{(within2/validResults.length*100).toFixed(1)}%</p>
            </div>
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 text-center">
              <p className="text-gray-400 text-sm">Within ±5 Years</p>
              <p className="text-yellow-400 text-3xl font-bold">{within5}</p>
              <p className="text-yellow-400 text-sm">{(within5/validResults.length*100).toFixed(1)}%</p>
            </div>
            <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 text-center">
              <p className="text-gray-400 text-sm">Within ±10 Years</p>
              <p className="text-blue-400 text-3xl font-bold">{within10}</p>
              <p className="text-blue-400 text-sm">{(within10/validResults.length*100).toFixed(1)}%</p>
            </div>
          </div>
        </div>

        {/* Results Grid */}
        {selectedPerson ? (
          <PersonDetail person={selectedPerson} onClose={() => setSelectedPerson(null)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedResults.map((person, index) => {
              const actual = person?.actual_data || {};
              const predicted = person?.predicted_marriage_age;
              const actualAge = actual?.first_marriage_age;
              const diff = actualAge ? Math.abs(predicted - actualAge) : null;
              
              let accuracyColor = 'text-red-400';
              let accuracyBg = 'bg-red-500/20';
              if (diff !== null) {
                if (diff <= 2) { accuracyColor = 'text-green-400'; accuracyBg = 'bg-green-500/20'; }
                else if (diff <= 5) { accuracyColor = 'text-yellow-400'; accuracyBg = 'bg-yellow-500/20'; }
                else if (diff <= 10) { accuracyColor = 'text-blue-400'; accuracyBg = 'bg-blue-500/20'; }
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedPerson(person)}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-left hover:bg-white/10 hover:border-purple-500/50 transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white group-hover:text-purple-400">
                        {person.name || 'Unknown'}
                      </h3>
                      <p className="text-gray-400 text-sm">{person.birth_date || 'N/A'}</p>
                    </div>
                    <div className={`${accuracyBg} px-2 py-1 rounded-full`}>
                      <span className={`${accuracyColor} text-sm font-medium`}>
                        {diff !== null ? `±${diff.toFixed(0)}y` : 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="bg-purple-500/10 rounded-lg p-2 text-center">
                      <p className="text-gray-500 text-xs">Predicted Age</p>
                      <p className="text-purple-400 font-bold text-lg">{predicted?.toFixed(1) || 'N/A'}</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-2 text-center">
                      <p className="text-gray-500 text-xs">Actual Age</p>
                      <p className="text-green-400 font-bold text-lg">{actualAge || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="mt-2 grid grid-cols-3 gap-1 text-xs">
                    <div className="bg-black/20 rounded p-1 text-center">
                      <span className="text-gray-500">Lagna</span>
                      <p className="text-white">{person.lagna || 'N/A'}</p>
                    </div>
                    <div className="bg-black/20 rounded p-1 text-center">
                      <span className="text-gray-500">7th Lord</span>
                      <p className="text-white">{person.seventh_lord || 'N/A'}</p>
                    </div>
                    <div className="bg-black/20 rounded p-1 text-center">
                      <span className="text-gray-500">Marriages</span>
                      <p className="text-white">{actual.total_marriages || 'N/A'}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const PersonDetail = ({ person, onClose }) => {
  const actual = person?.actual_data || {};
  const steps = person?.all_steps || {};
  
  const predicted = person?.predicted_marriage_age;
  const actualAge = actual?.first_marriage_age;
  const diff = actualAge ? Math.abs(predicted - actualAge) : null;

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white">{person.name}</h2>
            <p className="text-white/80">Born: {person.birth_date}</p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-white"
          >
            ← Back
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Accuracy Comparison */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-white mb-4">🎯 Prediction vs Reality</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-purple-500/20 rounded-lg p-4 text-center">
              <p className="text-gray-400 text-sm">Algorithm Predicted</p>
              <p className="text-purple-400 text-3xl font-bold">{predicted?.toFixed(1)}</p>
              <p className="text-gray-500 text-sm">years old</p>
            </div>
            <div className="bg-green-500/20 rounded-lg p-4 text-center">
              <p className="text-gray-400 text-sm">Actual First Marriage</p>
              <p className="text-green-400 text-3xl font-bold">{actualAge || 'N/A'}</p>
              <p className="text-gray-500 text-sm">years old</p>
            </div>
            <div className={`rounded-lg p-4 text-center ${
              diff <= 2 ? 'bg-green-500/20' : diff <= 5 ? 'bg-yellow-500/20' : diff <= 10 ? 'bg-blue-500/20' : 'bg-red-500/20'
            }`}>
              <p className="text-gray-400 text-sm">Difference</p>
              <p className={`text-3xl font-bold ${
                diff <= 2 ? 'text-green-400' : diff <= 5 ? 'text-yellow-400' : diff <= 10 ? 'text-blue-400' : 'text-red-400'
              }`}>
                {diff !== null ? `±${diff.toFixed(1)}` : 'N/A'}
              </p>
              <p className="text-gray-500 text-sm">years</p>
            </div>
          </div>
        </div>

        {/* Chart Details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <DetailCard label="Lagna" value={person.lagna} />
          <DetailCard label="Moon Nakshatra" value={person.moon_nakshatra} />
          <DetailCard label="7th Lord" value={person.seventh_lord} />
          <DetailCard label="Darakaraka" value={person.darakaraka} />
          <DetailCard label="Upapada" value={person.upapada} />
          <DetailCard label="Marriage Category" value={person.marriage_category} />
          <DetailCard label="Multiple Marriage" value={person.multiple_marriage_potential ? 'Yes' : 'No'} />
          <DetailCard label="Divorce Risk" value={`${person.divorce_probability || 0}%`} />
        </div>

        {/* Historical Data */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-white mb-3">📜 Historical Record</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-gray-500 text-sm">First Marriage Year</p>
              <p className="text-white font-bold">{actual.first_marriage_year || 'N/A'}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-gray-500 text-sm">Total Marriages</p>
              <p className="text-white font-bold">{actual.total_marriages || 'N/A'}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-gray-500 text-sm">Divorces</p>
              <p className="text-white font-bold">{actual.divorces || 0}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-center">
              <p className="text-gray-500 text-sm">Predicted Year</p>
              <p className="text-purple-400 font-bold">{person.predicted_marriage_year || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Marriage Windows */}
        {person.marriage_windows && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-3">⏰ Calculated Marriage Windows</h3>
            <div className="space-y-2">
              {person.marriage_windows.map((window, i) => (
                <div key={i} className={`rounded-lg p-3 ${
                  window.is_primary ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-black/20'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-medium ${window.is_primary ? 'text-purple-400' : 'text-white'}`}>
                        {window.dasha_lord} Mahadasha
                      </span>
                      {window.is_primary && <span className="ml-2 text-xs bg-purple-500/30 px-2 py-0.5 rounded text-purple-300">Primary</span>}
                    </div>
                    <span className="text-gray-400">
                      Ages {window.start_age} - {window.end_age}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mt-1">
                    {window.reason?.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Algorithm Steps */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-white mb-3">🔬 Algorithm Steps</h3>
          <div className="space-y-2">
            {Object.entries(steps).map(([key, value]) => (
              <details key={key} className="bg-black/20 rounded-lg overflow-hidden">
                <summary className="px-4 py-2 cursor-pointer text-white hover:bg-white/5">
                  {value?.name || key}
                </summary>
                <div className="px-4 py-2 bg-black/30 text-gray-400 text-sm">
                  <pre className="whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailCard = ({ label, value }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
    <p className="text-gray-500 text-xs">{label}</p>
    <p className="text-white font-medium">{value || 'N/A'}</p>
  </div>
);

export default AlgorithmValidation;
